import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { LoadingBoxComponent } from '../../../../shared/components/loading-box/loading-box.component';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';

import { ConfigKanbanService } from '../../../../shared/services/apis/vendas/config-kanban.service';
import { PerfilService } from '../../../../shared/services/apis/usuario/perfil.service';
import { KanbanProcessoStruct } from '../../../../shared/services/structs/vendas/kanban-processo.struct';
import { EtapaKanbanStruct } from '../../../../shared/services/structs/vendas/etapa-kanban.struct';
import { KanbanCor } from '../../../../shared/services/models/vendas/kanban-cor.model';
import { KanbanIcone } from '../../../../shared/services/models/vendas/kanban-icone.model';
import { TipoEtapaKanban } from '../../../../shared/services/models/vendas/tipo-etapa-kanban.model';
import { TipoProcessoKanban } from '../../../../shared/services/models/vendas/tipo-processo-kanban.model';
import { TipoProcessoKanbanEnum } from '../../../../shared/enums/vendas/tipo-processo-kanban.enum';
import { TipoEtapaKanbanEnum } from '../../../../shared/enums/vendas/tipo-etapa-kanban.enum';
import { PerfilOptionStruct } from '../../../../shared/services/structs/usuario/perfil-option.struct';

import { KanbanProcessoColunaComponent, KanbanToggleAtivoEvent } from './kanban-processo-coluna/kanban-processo-coluna.component';
import { KanbanAviso, validarKanban } from './kanban-validacao.util';
import {
  EtapaKanbanModalComponent,
  EtapaKanbanModalResult,
} from './dialogs/etapa-kanban-modal/etapa-kanban-modal.component';
import {
  EtapaKanbanExcluirDialogComponent,
  EtapaKanbanExcluirDialogResult,
} from './dialogs/etapa-kanban-excluir-dialog/etapa-kanban-excluir-dialog.component';

@Component({
  selector: 'app-config-kanban',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatDialogModule,
    MatSlideToggleModule,
    HeaderComponent,
    LoadingBoxComponent,
    KanbanProcessoColunaComponent,
  ],
  templateUrl: './config-kanban.component.html',
  styleUrls: ['./config-kanban.component.css'],
})
export class ConfigKanbanComponent implements OnInit {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Vendas;

  public isLoading = false;
  public processos: KanbanProcessoStruct[] = [];
  public cores: KanbanCor[] = [];
  public icones: KanbanIcone[] = [];
  public tiposEtapaKanban: TipoEtapaKanban[] = [];
  public tipos: TipoProcessoKanban[] = [];
  public iconesById = new Map<number, string>();
  public coresById = new Map<number, KanbanCor>();
  public perfis: PerfilOptionStruct[] = [];
  public avisos: KanbanAviso[] = [];

  public isMostrarSomaColuna = true;
  public isSalvandoSomaColuna = false;

  // Arrays usados no skeleton loader — a tela tem exatamente 2 processos.
  readonly skeletonCols = [[1, 2, 3, 4, 5, 6], [1, 2, 3, 4, 5]];

  // Ícone e subtítulo por tipo de processo — apresentação da tela, não dado de negócio.
  private readonly tipoIcones: Record<number, string> = {
    [TipoProcessoKanbanEnum.Orcamento]: 'description',
    [TipoProcessoKanbanEnum.PedidoDeVenda]: 'shopping_cart',
  };
  private readonly tipoSubtitulos: Record<number, string> = {
    [TipoProcessoKanbanEnum.Orcamento]: 'Processo de orçamento',
    [TipoProcessoKanbanEnum.PedidoDeVenda]: 'Processo comercial',
  };

  public mostrarAjuda = false;
  public avisosMinimizados = false;

  constructor(
    private configKanbanService: ConfigKanbanService,
    private perfilService: PerfilService,
    private alertService: AlertService,
    private dialog: MatDialog,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.carregarTudo();
  }

  public get temAvisos(): boolean {
    return this.avisos.length > 0;
  }

  public tipoIcone(idTipoProcessoKanban: number): string {
    return this.tipoIcones[idTipoProcessoKanban] ?? 'category';
  }

  public tipoSubtitulo(idTipoProcessoKanban: number): string {
    return this.tipoSubtitulos[idTipoProcessoKanban] ?? '';
  }

  /** Obrigatoriedade é dado do lookup, não regra hardcoded no frontend. */
  public isProcessoObrigatorio(idTipoProcessoKanban: number): boolean {
    return this.tipos.find(t => t.idTipoProcessoKanban === idTipoProcessoKanban)?.isObrigatorio ?? false;
  }

  private carregarTudo(): void {
    this.isLoading = true;
    forkJoin({
      config: this.configKanbanService.get().pipe(catchError(() => of(null))),
      lookups: this.configKanbanService.getLookups().pipe(catchError(() => of(null))),
      perfis: this.perfilService.getAllLookup().pipe(catchError(() => of(null))),
    }).subscribe({
      next: res => {
        this.isLoading = false;
        if (res.lookups && !res.lookups.isError) {
          this.cores = res.lookups.cores || [];
          this.icones = res.lookups.icones || [];
          this.tipos = res.lookups.tipos || [];
          this.tiposEtapaKanban = res.lookups.tiposEtapaKanban || [];
          this.iconesById = new Map(this.icones.map(i => [i.idKanbanIcone, i.nomeIcone]));
          this.coresById = new Map(this.cores.map(c => [c.idKanbanCor, c]));
        } else {
          // Sem os catálogos a tela não tem cor, ícone nem título de seção. Falhar em silêncio
          // deixaria o usuário com um board cinza sem explicação.
          this.alertService.show('Aviso', res.lookups?.errorDescription
            || 'Não foi possível carregar os catálogos de cores e ícones.', AlertType.warning);
        }
        if (res.perfis && !res.perfis.isError) {
          this.perfis = (res.perfis.listaPerfil || []).map(p => ({ idPerfil: p.perfil.idPerfil, nome: p.perfil.nome }));
        } else if (res.perfis?.isError) {
          this.alertService.show('Aviso', res.perfis.errorDescription, AlertType.warning);
        }
        if (res.config && !res.config.isError) {
          this.processos = res.config.processos || [];
          this.isMostrarSomaColuna = res.config.isMostrarSomaColuna;
          this.avisos = validarKanban(this.processos);
        } else if (res.config?.isError) {
          this.alertService.show('Aviso', res.config.errorDescription, AlertType.warning);
        }
      },
      error: () => {
        this.isLoading = false;
        this.alertService.show('Aviso', 'Falha na comunicação com o servidor.', AlertType.warning);
      },
    });
  }

  private recarregarConfig(): void {
    this.configKanbanService.get().subscribe({
      next: res => {
        if (res.isError) {
          this.alertService.show('Aviso', res.errorDescription, AlertType.warning);
          return;
        }
        this.processos = res.processos || [];
        this.isMostrarSomaColuna = res.isMostrarSomaColuna;
        this.avisos = validarKanban(this.processos);
      },
      error: () => this.alertService.show('Aviso', 'Falha ao recarregar.', AlertType.warning),
    });
  }

  // ---- Ações vindas das colunas ----

  public adicionar(processo: KanbanProcessoStruct): void {
    this.abrirModalEtapa(null, processo);
  }

  public editar(processo: KanbanProcessoStruct, etapa: EtapaKanbanStruct): void {
    this.abrirModalEtapa(etapa, processo);
  }

  private abrirModalEtapa(etapa: EtapaKanbanStruct | null, processo: KanbanProcessoStruct): void {
    const ref = this.dialog.open(EtapaKanbanModalComponent, {
      width: '880px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      autoFocus: false,
      data: {
        etapa,
        idTipoProcessoKanban: processo.idTipoProcessoKanban,
        tipoNome: processo.descricaoTipo,
        tipoIcones: this.tipoIcones,
        processos: this.processos,
        cores: this.cores,
        icones: this.icones,
        perfis: this.perfis,
      },
    });

    ref.afterClosed().subscribe((result: EtapaKanbanModalResult) => {
      if (!result) return;
      if (result.action === 'saved') {
        this.recarregarConfig();
      } else if (result.action === 'delete') {
        this.fluxoExcluir(result.idEtapaKanban);
      }
    });
  }

  public excluir(etapa: EtapaKanbanStruct): void {
    this.fluxoExcluir(etapa.idEtapaKanban);
  }

  private fluxoExcluir(idEtapaKanban: number): void {
    const processoAtual = this.processos.find(p => p.etapas.some(s => s.idEtapaKanban === idEtapaKanban));
    const etapaAtual = processoAtual?.etapas.find(s => s.idEtapaKanban === idEtapaKanban);
    if (!processoAtual || !etapaAtual) return;

    // Etapa fixa não chega aqui (o menu não oferece Excluir), mas a trava não pode depender disso.
    if (etapaAtual.isFixo) {
      this.alertService.show('Atenção',
        `«${etapaAtual.nome}» é uma etapa fixa do sistema e não pode ser excluída.`, AlertType.warning);
      return;
    }

    // Bloqueios que impedem o diálogo de abrir (seção 5.1 da story).
    if (etapaAtual.isInicial) {
      this.alertService.show('Atenção',
        `«${etapaAtual.nome}» é a etapa inicial de ${processoAtual.descricaoTipo}. `
        + 'Marque outra etapa como inicial antes de excluí-la.', AlertType.warning);
      return;
    }

    // O processo não pode ficar sem fluxo principal.
    const qtdFluxoPrincipal = processoAtual.etapas.filter(
      s => s.idTipoEtapaKanban === TipoEtapaKanbanEnum.FluxoPrincipal
    ).length;

    if (etapaAtual.idTipoEtapaKanban === TipoEtapaKanbanEnum.FluxoPrincipal && qtdFluxoPrincipal <= 1) {
      this.alertService.show('Atenção',
        `O processo ${processoAtual.descricaoTipo} precisa de ao menos uma etapa no fluxo principal. `
        + `Crie outra etapa antes de excluir «${etapaAtual.nome}».`, AlertType.warning);
      return;
    }

    const qtd = this.processos.flatMap(p => p.etapas).flatMap(s => s.movimentacoes)
      .filter(m => m.idEtapaKanbanDestino === idEtapaKanban).length;

    const ref = this.dialog.open(EtapaKanbanExcluirDialogComponent, {
      width: '480px',
      data: {
        nomeEtapa: etapaAtual.nome,
        nomeProcesso: processoAtual.descricaoTipo,
        qtdMovimentacoesApontando: qtd,
      },
    });

    ref.afterClosed().subscribe((result?: EtapaKanbanExcluirDialogResult) => {
      if (!result?.confirm) return;
      this.executarExclusao(processoAtual, idEtapaKanban);
    });
  }

  /**
   * Exclusão lógica, sem migração de documentos nem inativar: etapa com documento (ou com outro
   * impedimento, como vendas em Envio e retirada que voltam para ela) não se exclui. Quem confere é a
   * API, e a recusa volta em isError com o que mover antes, uma linha por impedimento.
   */
  private executarExclusao(processoAtual: KanbanProcessoStruct, idEtapaKanban: number): void {
    this.configKanbanService
      .excluirEtapa({ idEtapaKanban })
      .subscribe({
        next: r => {
          if (r.isError) {
            // A recusa pode trazer um impedimento por linha; o alerta renderiza HTML.
            this.alertService.show('Erro', (r.errorDescription ?? '').split('\n').join('<br>'), AlertType.error);
            return;
          }
          this.alertService.show('Sucesso', 'Etapa excluída com sucesso.', AlertType.success);
          // Remoção otimista: o backend já confirmou, então o card some da tela imediatamente —
          // não depende do GET de recarregarConfig() chegar/renderizar a tempo.
          processoAtual.etapas = processoAtual.etapas.filter(s => s.idEtapaKanban !== idEtapaKanban);
          this.processos = [...this.processos];
          this.avisos = validarKanban(this.processos);
          this.recarregarConfig();
        },
        error: err => this.alertService.show('Erro', err.message || 'Erro ao excluir.', AlertType.error),
      });
  }

  public reordenar(idsOrdenados: number[]): void {
    this.configKanbanService.reordenar({ idsOrdenados }).subscribe({
      next: r => {
        if (r.isError) {
          this.alertService.show('Aviso', r.errorDescription, AlertType.warning);
          this.recarregarConfig();
          return;
        }
        this.avisos = validarKanban(this.processos);
      },
      error: () => this.recarregarConfig(),
    });
  }

  public toggleProcesso(processo: KanbanProcessoStruct, evento: KanbanToggleAtivoEvent): void {
    const isAtivoAnterior = processo.isAtivo;
    this.configKanbanService.toggleProcesso(processo.idTipoProcessoKanban, { isAtivo: evento.isAtivo }).subscribe({
      next: r => {
        if (r.isError) {
          this.alertService.show('Aviso', r.errorDescription, AlertType.warning);
          if (evento.toggleRef) evento.toggleRef.checked = isAtivoAnterior;
          return;
        }
        this.recarregarConfig();
      },
      error: () => {
        this.alertService.show('Aviso', 'Falha na comunicação com o servidor.', AlertType.warning);
        if (evento.toggleRef) evento.toggleRef.checked = isAtivoAnterior;
      },
    });
  }

  public toggleSomaColuna(isMostrarSomaColuna: boolean): void {
    const valorAnterior = this.isMostrarSomaColuna;
    this.isMostrarSomaColuna = isMostrarSomaColuna;
    // Diferente do toggle de processo (que recarrega a coluna inteira e a mudança visual já
    // confirma o salvamento), este toggle não tem nenhum outro efeito nesta tela — sem toast,
    // sucesso e silêncio ficam indistinguíveis pro usuário.
    this.isSalvandoSomaColuna = true;
    this.configKanbanService.salvarMostrarSomaColuna({ isMostrarSomaColuna }).subscribe({
      next: r => {
        this.isSalvandoSomaColuna = false;
        if (r.isError) {
          this.alertService.show('Aviso', r.errorDescription, AlertType.warning);
          this.isMostrarSomaColuna = valorAnterior;
          return;
        }
        this.alertService.show('Sucesso', 'Preferência salva.', AlertType.success);
      },
      error: () => {
        this.isSalvandoSomaColuna = false;
        this.alertService.show('Aviso', 'Falha na comunicação com o servidor.', AlertType.warning);
        this.isMostrarSomaColuna = valorAnterior;
      },
    });
  }

  public focarAviso(aviso: KanbanAviso): void {
    if (!aviso.idEtapaKanban) return;
    const el = document.getElementById('etapa-kanban-' + aviso.idEtapaKanban);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('etapa-card-highlight');
    setTimeout(() => el.classList.remove('etapa-card-highlight'), 1500);
  }

  public voltar(): void {
    this.router.navigate(['/configuracoes/vendas']);
  }
}
